import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Tab, Box, Grid, Typography, Button, Modal, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, CircularProgress } from '@mui/material';
import DomainsTable from "./DomainsTable"
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import { BRAND_TYPE, SAFE_SENDER_CLIENT_CODE, SAFE_SENDER_REDIRECT_URI, ALLOWLIST_CLIENT_CODE, ALLOWLIST_REDIRECT_URI } from "../../config";
import { useSelector } from "react-redux";
import { useGetAllowlistingStatusQuery, useRevokeAllowlistingMutation } from "../../store/services/tenancy.service";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";



function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`reports-tabpanel-${index}`}
            aria-labelledby={`reports-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `reports-tab-${index}`,
        'aria-controls': `reports-tabpanel-${index}`,
    };
}



export default function Deliverability({ data }) {
    const [subTabValue, setSubTabValue] = useState(0);
    const [ipModalOpen, setIpModalOpen] = useState(false);
    const [manualStepsOpen, setManualStepsOpen] = useState(false);
    // Which manual rows have been copied. Separate from copiedStates, which is the
    // 1.5 second tick every copy button in this file uses: these have to persist so
    // you can see how far through 49 domains you are. Cleared when the modal closes,
    // since the list is a working checklist rather than a record of anything.
    const [manualCopied, setManualCopied] = useState({});
    const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
    const [connectInfoOpen, setConnectInfoOpen] = useState(false);
    const [copiedStates, setCopiedStates] = useState({});
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    
    // List of IPs to display
    const sendingIPs = [
        "23.249.219.118",
        "18.168.104.87",
        "13.42.200.223",
        "3.9.228.40",
        "3.127.7.20",
        "63.178.172.172",
        "13.216.31.253",
        "74.177.142.1"
    ];
    
    const copyToClipboard = useCallback(async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [id]: true }));
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [id]: false }));
            }, 1500);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, []);
    
    
    const copyManualItem = useCallback(async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setManualCopied(prev => ({ ...prev, [id]: true }));
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, []);

    const closeManualSteps = useCallback(() => {
        setManualStepsOpen(false);
        setManualCopied({});
    }, []);

    // Get tenancy ID and feature switches
    const { tenancyId, featureSwitches } = useSelector((state) => state.tenancy);

    // Starts at /adminconsent; auth-service chains on to /authorize.
    const startSafeSenderConnect = () => {
        const redirectUri = encodeURIComponent(SAFE_SENDER_REDIRECT_URI);
        const scope = encodeURIComponent("https://graph.microsoft.com/.default");
        window.open(
            `https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=${SAFE_SENDER_CLIENT_CODE}&redirect_uri=${redirectUri}&scope=${scope}&state=${tenancyId}`,
            "_blank"
        );
    };

    // Allowlisting Sync (BD-740). Same consent shape as Safe Sender, different app registration.
    const startAllowlistingConnect = () => {
        const redirectUri = encodeURIComponent(ALLOWLIST_REDIRECT_URI);
        const scope = encodeURIComponent("https://graph.microsoft.com/.default");
        window.open(
            `https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=${ALLOWLIST_CLIENT_CODE}&redirect_uri=${redirectUri}&scope=${scope}&state=${tenancyId}`,
            "_blank"
        );
    };

    const allowlistingEnabled = Boolean(featureSwitches?.enableAllowlistingSync);

    // The in-flight state lives in the run status the backend writes before it invokes
    // the runner, not in component state, so a refresh or a different browser still
    // shows the run in progress. Polling only runs while something is in flight.
    const [allowlistingPollMs, setAllowlistingPollMs] = useState(0);
    const { data: allowlistingStatus, isFetching: allowlistingStatusFetching } = useGetAllowlistingStatusQuery(undefined, {
        skip: !allowlistingEnabled,
        pollingInterval: allowlistingPollMs
    });

    const [revokeAllowlisting, { isLoading: allowlistingRevokeStarting }] = useRevokeAllowlistingMutation();

    const allowlistingRunStatus = allowlistingStatus?.runStatus;

    // A runner that dies without writing a result would otherwise leave the panel on a
    // spinner with no buttons and no way out but a database edit. The backend stamps
    // LastRunAt when it marks a run in flight, so anything older than the Lambda's own
    // 15 minute ceiling is treated as finished regardless of what the status says.
    const allowlistingRunStartedAt = Date.parse(allowlistingStatus?.lastRunAt ?? "");
    const allowlistingRunStale = Number.isFinite(allowlistingRunStartedAt)
        && (Date.now() - allowlistingRunStartedAt) > 20 * 60 * 1000;

    const allowlistingStatusInFlight = !allowlistingRunStale
        && (allowlistingRunStatus === "Revoking"
            || allowlistingRunStatus === "Pending"
            || allowlistingRunStatus === "Running");

    // allowlistingRevokeStarting covers the gap between the click and the refetch that
    // first reports Revoking, so the panel never falls back to buttons in between.
    const allowlistingInFlight = allowlistingStatusInFlight || allowlistingRevokeStarting;
    const allowlistingRevoking = allowlistingRunStatus === "Revoking" || allowlistingRevokeStarting;
    const allowlistingRevokeFailed = allowlistingRunStatus === "RevokeFailed";

    useEffect(() => {
        setAllowlistingPollMs(allowlistingInFlight ? 5000 : 0);
    }, [allowlistingInFlight]);

    // The runner tags each note with the surface it belongs to and joins them with
    // newlines, so the panel can group them instead of printing one run-on paragraph.
    // Runs from before that change arrive space-joined and fall through as one line.
    const allowlistingDetailLines = (allowlistingStatus?.lastRunDetail || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    const allowlistingFailures = allowlistingDetailLines
        .filter((line) => line.startsWith("FAILED - "))
        .map((line) => line.slice("FAILED - ".length));
    const allowlistingManualNotes = allowlistingDetailLines
        .filter((line) => line.startsWith("MANUAL: "))
        .map((line) => line.slice("MANUAL: ".length));
    const allowlistingDefenderNotes = allowlistingDetailLines
        .filter((line) => line.startsWith("DEFENDER: "))
        .map((line) => line.slice("DEFENDER: ".length));
    const manualCopiedIpCount = (allowlistingStatus?.manualSendingIps || [])
        .filter((ip) => manualCopied[`ip-${ip}`]).length;
    const manualCopiedDomainCount = (allowlistingStatus?.manualDomains || [])
        .filter((d) => manualCopied[`domain-${d}`]).length;

    const allowlistingMailFlowNotes = allowlistingDetailLines
        .filter((line) => !line.startsWith("FAILED - ")
            && !line.startsWith("MANUAL: ")
            && !line.startsWith("DEFENDER: "));

    // Removal takes minutes like an apply does, so the button only kicks it off; the
    // panel below reflects whatever the runner reported. Behind a confirmation
    // because it tears down live Exchange configuration in the customer's tenant.
    const triggerAllowlistingRevoke = async () => {
        setRevokeConfirmOpen(false);
        try {
            await revokeAllowlisting().unwrap();
        } catch (err) {
            console.error("Failed to start allowlisting removal: ", err);
        }
    };

    // Define all possible tabs with their feature flags
    const allTabs = [
        { 
            label: "Sending Information", 
            component: (
                <>
                    <MainCard content={true} boxShadow={true} border={false}
                        sx={{
                            height: "100%",
                            bgcolor: getCardColor(lightMode),
                            mb: 3
                        }}>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between"
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Typography variant="h5">Sending IPs:</Typography>
                                <Button 
                                    variant="contained" 
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => setIpModalOpen(true)}
                                    size="small"
                                >
                                    View
                                </Button>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "left" }}>
                                <Typography variant="h5">
                                    Header key: X-BPSAT
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "left" }}>
                                <Typography variant="h5">
                                    Header value: 66Y7U72LTS
                                </Typography>
                            </Box>
                        </Box>
                    </MainCard>
                    <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                        <Grid item xs={12} md={12} lg={12}>
                            <DomainsTable />
                        </Grid>
                    </Grid>
                </>
            ),
            featureFlag: 'enableSendingInfo'
        },
        { 
            label: "Microsoft", 
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title={`microsoft`}
                    src={BRAND_TYPE === "censornet" ? 'https://help.clouduss.com/safelisting/microsoft-365-sat-safelisting' : 'https://boxphishsupport.helpdocs.io/article/d1g40drjo3-m-365-allowlisting' }
                />
            ),
            featureFlag: 'enableMicrosoftIntegration' 
        },
        { 
            label: "Google", 
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title={`google`}
                    src={BRAND_TYPE === "censornet" ? 'https://help.clouduss.com/safelisting/google-workspace-sat-safelisting' : 'https://boxphishsupport.helpdocs.io/article/gr96dprgbo-google-workspace'}
                />
            ),
            featureFlag: 'enableGoogleIntegration' 
        },
        { 
            label: "Gateways", 
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title={`gateways`}
                    src={'https://boxphishsupport.helpdocs.io/article/zbvzcguyaw-allow-listing-links'}
                />
            ),
            featureFlag: 'enableGateways',
            brandRestricted: true // Only for boxphish brand
        },
        {
            label: "Safe Sender Sync",
            component: (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">Safe Sender Sync</Typography>
                            </Box>
                            <Box mb={3} mt={1}>
                                <Typography variant="caption">
                                    Automatically add our phishing simulation sending domains to your users' Safe Senders list in Exchange Online, so simulated emails display correctly. Connect once with a Privileged Role Administrator and new starters are covered automatically - removing the need to apply the Safe Senders updates manually each time a user is added. Once connected, your users' Safe Senders lists are updated within 24 hours.
                                </Typography>
                            </Box>
                            <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center">
                                <Button
                                    onClick={startSafeSenderConnect}
                                    variant="contained"
                                    color="warning"
                                    sx={{ width: "80%" }}
                                >
                                    Connect to Microsoft
                                </Button>
                            </Box>
                        </MainCard>
                    </Grid>
                </Grid>
            ),
            featureFlag: 'enableSafeSenderSync'
        },
        {
            label: "Allowlisting Sync",
            component: (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">Allowlisting Sync</Typography>
                                {allowlistingStatus?.connected && (
                                    allowlistingInFlight ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {allowlistingRevoking ? "Revoking" : "Applying"}
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="success.main">Connected</Typography>
                                    )
                                )}
                            </Box>
                            <Box mb={3} mt={1}>
                                <Typography variant="caption">
                                    Connect once with a Global Administrator and we apply the Exchange Online mail flow
                                    changes our phishing simulations need, plus the simulation URLs in Microsoft Defender.
                                    A run takes a few minutes. One part cannot be automated: the Defender Advanced Delivery
                                    sending domains and IPs have to be entered by hand, and until they are, simulations may
                                    still be filtered.
                                </Typography>
                            </Box>
                            {allowlistingStatus?.connected ? (
                                <Box mb={2}>
                                    <List dense disablePadding>
                                        {allowlistingStatus.onMicrosoftDomain && (
                                            <ListItem disableGutters>
                                                <ListItemText primary="Tenant" secondary={allowlistingStatus.onMicrosoftDomain} />
                                            </ListItem>
                                        )}
                                        <ListItem disableGutters>
                                            <ListItemText primary="Last run" secondary={allowlistingStatus.runStatus || "Not run yet"} />
                                        </ListItem>
                                        {allowlistingStatus.lastRunAt && (
                                            <ListItem disableGutters>
                                                <ListItemText primary="Last run at" secondary={allowlistingStatus.lastRunAt} />
                                            </ListItem>
                                        )}
                                    </List>

                                    {allowlistingFailures.length > 0 && (
                                        <Box mt={2}>
                                            <Typography variant="subtitle2" color="error.main" gutterBottom>
                                                <strong>Did not apply</strong>
                                            </Typography>
                                            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                                {allowlistingFailures.map((line) => (
                                                    <li key={line}>
                                                        <Typography variant="caption" color="error.main">{line}</Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {allowlistingMailFlowNotes.length > 0 && (
                                        <Box mt={2}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                <strong>Exchange Online mail flow</strong>
                                            </Typography>
                                            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                                {allowlistingMailFlowNotes.map((line) => (
                                                    <li key={line}>
                                                        <Typography variant="caption">{line}</Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                !allowlistingStatusFetching && (
                                    <Box mb={2}>
                                        <Typography variant="caption" color="text.secondary">Not connected yet.</Typography>
                                    </Box>
                                )
                            )}

                            {/* Always shown, connected or not: the manual half is the thing people
                                most need to know about before they start, not only after a run. */}
                            <Box mb={3}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Microsoft Defender Advanced Delivery</strong>
                                </Typography>
                                {allowlistingDefenderNotes.length > 0 && (
                                    <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1 }}>
                                        {allowlistingDefenderNotes.map((line) => (
                                            <li key={line}>
                                                <Typography variant="caption">{line}</Typography>
                                            </li>
                                        ))}
                                    </Box>
                                )}
                                <Typography variant="caption" color="warning.main" component="div" sx={{ mb: 1 }}>
                                    {allowlistingManualNotes.length > 0
                                        ? allowlistingManualNotes.join(" ")
                                        : "The sending domains and sending IPs must be entered by hand. Microsoft does not permit this to be automated."}
                                    {" "}Until that is done, simulations may still be filtered.
                                </Typography>
                                <Button
                                    onClick={() => setManualStepsOpen(true)}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                >
                                    View the manual steps
                                </Button>
                            </Box>
                            {allowlistingRevokeFailed && (
                                <Box mb={2}>
                                    <Typography variant="caption" color="error.main">
                                        <strong>The last removal did not finish.</strong> Some of what we applied is still in
                                        your Exchange Online - the detail above says what. Revoke again to retry.
                                    </Typography>
                                </Box>
                            )}
                            {allowlistingInFlight ? (
                                /* No buttons at all while a run is in flight: Connect would queue a
                                   second run against the same tenant, which the runner is not safe
                                   against. The state comes from the server, so this survives a refresh. */
                                <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={1}>
                                    <CircularProgress size={28} color={allowlistingRevoking ? "error" : "warning"} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {allowlistingRevoking ? "Revoking allowlisting" : "Applying allowlisting"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" align="center">
                                        {allowlistingRevoking
                                            ? "Removing the Boxphish transport rule, inbound connector and simulation URLs from your Exchange Online."
                                            : "Adding the Boxphish transport rule, inbound connector and simulation URLs to your Exchange Online."}
                                        {" "}This takes a few minutes and carries on if you leave this page.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center" gap={1}>
                                    <Button
                                        onClick={() => setConnectInfoOpen(true)}
                                        variant="contained"
                                        color="warning"
                                        sx={{ width: "80%" }}
                                    >
                                        {allowlistingStatus?.connected ? "Reconnect to Microsoft" : "Connect to Microsoft"}
                                    </Button>
                                    {allowlistingStatus?.connected && (
                                        <Button
                                            onClick={() => setRevokeConfirmOpen(true)}
                                            variant="outlined"
                                            color="error"
                                            sx={{ width: "80%" }}
                                        >
                                            Revoke allowlisting
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </MainCard>
                    </Grid>
                </Grid>
            ),
            featureFlag: 'enableAllowlistingSync'
        }
    ];

    // Filter tabs based on feature switches and brand
    const visibleTabs = allTabs.filter(tab => {
        // Check brand restriction
        if (tab.brandRestricted && BRAND_TYPE !== "boxphish") {
            return false;
        }
        // Check feature flag
        if (tab.featureFlag && (!featureSwitches || !featureSwitches[tab.featureFlag])) {
            return false;
        }
        return true;
    });

    const handleSubTabChange = (event, newValue) => {
        setSubTabValue(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="deliverability tabs">
                    {visibleTabs.map((tab, index) => (
                        <Tab key={index} label={tab.label} {...a11yProps(index)} />
                    ))}
                </Tabs>
            </Box>
            {visibleTabs.map((tab, index) => (
                <TabPanel key={index} value={subTabValue} index={index}>
                    {tab.component}
                </TabPanel>
            ))}
            
            {/* IP Display Modal */}
            <Modal
                open={ipModalOpen}
                onClose={() => setIpModalOpen(false)}
                aria-labelledby="ip-modal-title"
                aria-describedby="ip-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 500,
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.borderRadius || 1,
                        p: 0,
                        outline: 'none'
                    }}
                >
                    <Box 
                        sx={{ 
                            display: "flex", 
                            justifyContent: "space-between", 
                            alignItems: "center",
                            p: 3,
                            pb: 2
                        }}
                    >
                        <Typography id="ip-modal-title" variant="h4">
                            Sending IP Addresses
                        </Typography>
                        <IconButton 
                            onClick={() => setIpModalOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ p: 3, pt: 2 }}>
                        <List sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1, p: 1 }}>
                            {sendingIPs.map((ip, index) => (
                                <React.Fragment key={ip}>
                                    <ListItem 
                                        sx={{ 
                                            py: 1.5,
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                                                borderRadius: 1
                                            }
                                        }}
                                    >
                                        <ListItemText 
                                            primary={ip}
                                            primaryTypographyProps={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.95rem'
                                            }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => copyToClipboard(ip, ip)}
                                                size="small"
                                            >
                                                {copiedStates[ip] ? 
                                                    <CheckIcon fontSize="small" color="success" /> : 
                                                    <ContentCopyIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < sendingIPs.length - 1 && <Divider variant="middle" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Box>
            </Modal>

            {/* Advanced Delivery domains and IPs cannot be set through the API, so the
                admin copies them from here. The lists come from the status endpoint,
                which reads the same secret the runner uses, so they cannot drift from
                what we actually send from. */}
            <Modal
                open={manualStepsOpen}
                onClose={closeManualSteps}
                aria-labelledby="manual-steps-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 640,
                        maxHeight: "85vh",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.borderRadius || 1,
                        outline: 'none'
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 3, pb: 2 }}>
                        <Typography id="manual-steps-title" variant="h4">
                            Advanced Delivery: manual steps
                        </Typography>
                        <IconButton onClick={closeManualSteps} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 3, pt: 2, overflowY: "auto" }}>
                        <Typography variant="body2" paragraph>
                            In the Microsoft Defender portal go to <strong>Policies &amp; rules</strong> &rarr;{" "}
                            <strong>Threat policies</strong> &rarr; <strong>Advanced delivery</strong> &rarr;{" "}
                            <strong>Phishing simulation</strong>, then click <strong>Edit</strong>.
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Microsoft requires <strong>at least one sending domain and at least one sending IP</strong>.
                            With either list empty the override does nothing. The simulation URLs are already added for you.
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Defender only accepts <strong>one entry at a time</strong>, so copy each row with the button
                            beside it. Copy all is there for the PowerShell route, which does take the whole list.
                        </Typography>

                        <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
                            Sending IPs ({manualCopiedIpCount} of {(allowlistingStatus?.manualSendingIps || []).length} copied)
                        </Typography>
                        <Button
                            size="small"
                            startIcon={copiedStates['all-ips'] ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                            onClick={() => copyToClipboard((allowlistingStatus?.manualSendingIps || []).join('\n'), 'all-ips')}
                            sx={{ mb: 1 }}
                        >
                            Copy all
                        </Button>
                        <List dense sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1, p: 1 }}>
                            {(allowlistingStatus?.manualSendingIps || []).map((ip) => (
                                <ListItem key={ip} sx={{ py: 0.5, pr: 6 }}>
                                    <ListItemText
                                        primary={ip}
                                        primaryTypographyProps={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.9rem',
                                            sx: manualCopied[`ip-${ip}`]
                                                ? { textDecoration: 'line-through', color: 'text.disabled' }
                                                : undefined
                                        }}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() => copyManualItem(ip, `ip-${ip}`)}
                                            size="small"
                                            aria-label={`Copy ${ip}`}
                                        >
                                            {manualCopied[`ip-${ip}`] ?
                                                <CheckIcon fontSize="small" color="success" /> :
                                                <ContentCopyIcon fontSize="small" />
                                            }
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>

                        <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
                            Sending domains ({manualCopiedDomainCount} of {(allowlistingStatus?.manualDomains || []).length} copied)
                        </Typography>
                        <Button
                            size="small"
                            startIcon={copiedStates['all-domains'] ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                            onClick={() => copyToClipboard((allowlistingStatus?.manualDomains || []).join('\n'), 'all-domains')}
                            sx={{ mb: 1 }}
                        >
                            Copy all
                        </Button>
                        <List dense sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1, p: 1 }}>
                            {(allowlistingStatus?.manualDomains || []).map((d) => (
                                <ListItem key={d} sx={{ py: 0.5, pr: 6 }}>
                                    <ListItemText
                                        primary={d}
                                        primaryTypographyProps={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.9rem',
                                            sx: manualCopied[`domain-${d}`]
                                                ? { textDecoration: 'line-through', color: 'text.disabled' }
                                                : undefined
                                        }}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() => copyManualItem(d, `domain-${d}`)}
                                            size="small"
                                            aria-label={`Copy ${d}`}
                                        >
                                            {manualCopied[`domain-${d}`] ?
                                                <CheckIcon fontSize="small" color="success" /> :
                                                <ContentCopyIcon fontSize="small" />
                                            }
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Box>
            </Modal>

            {/* Shown before the redirect to Microsoft. What we create in the customer's
                tenant, and what they can change afterwards, belongs in front of the
                admin at the moment they authorise it rather than as standing text
                nobody reads. The button inside performs the actual redirect. */}
            <Modal
                open={connectInfoOpen}
                onClose={() => setConnectInfoOpen(false)}
                aria-labelledby="connect-info-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 560,
                        maxHeight: "85vh",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.borderRadius || 1,
                        outline: 'none'
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 3, pb: 2 }}>
                        <Typography id="connect-info-title" variant="h4">
                            What we will create
                        </Typography>
                        <IconButton onClick={() => setConnectInfoOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 3, pt: 2, overflowY: "auto" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <strong>Exchange Online mail flow</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                            <li>
                                <Typography variant="body2">
                                    A transport rule named <strong>Boxphish Header Allow</strong>, created at{" "}
                                    <strong>priority 0</strong> so it is evaluated before your other mail flow rules.
                                    You can change its priority afterwards and we will not override it on later runs.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    An inbound connector named <strong>Boxphish Trusted Connector</strong>, trusting our
                                    sending IPs and domains.
                                </Typography>
                            </li>
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                            <strong>Microsoft Defender Advanced Delivery</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                            <li>
                                <Typography variant="body2">
                                    Our simulation URLs, added for you.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="warning.main">
                                    The sending domains and sending IPs are <strong>not</strong> added for you. Microsoft
                                    does not permit it, so you enter those by hand afterwards.
                                </Typography>
                            </li>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            You will be asked to sign in as a Global Administrator. Nothing is changed in your tenant
                            until you consent, and everything above can be removed later with Revoke.
                        </Typography>
                    </Box>

                    <Divider />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <Button onClick={() => setConnectInfoOpen(false)} variant="outlined">
                            Back
                        </Button>
                        <Button
                            onClick={() => { setConnectInfoOpen(false); startAllowlistingConnect(); }}
                            variant="contained"
                            color="warning"
                        >
                            I have read and understand, connect to Microsoft
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Revoke confirmation. Spells out exactly what is removed and what the
                customer still has to do by hand, because the two things we cannot
                undo from here (the Defender entries and the Entra consent) are the
                ones an admin would assume "revoke" covers. */}
            <Modal
                open={revokeConfirmOpen}
                onClose={() => setRevokeConfirmOpen(false)}
                aria-labelledby="revoke-confirm-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 560,
                        maxHeight: "85vh",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.borderRadius || 1,
                        outline: 'none'
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 3, pb: 2 }}>
                        <Typography id="revoke-confirm-title" variant="h4">
                            Revoke allowlisting?
                        </Typography>
                        <IconButton onClick={() => setRevokeConfirmOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 3, pt: 2, overflowY: "auto" }}>
                        <Typography variant="body2" paragraph>
                            This removes the following from your Exchange Online, and your
                            simulations will start being filtered again:
                        </Typography>
                        <List dense sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1, p: 1, mb: 2 }}>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText primary="The transport rule &quot;Boxphish Header Allow&quot;" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText primary="The inbound connector &quot;Boxphish Trusted Connector&quot;" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText primary="The Boxphish simulation URLs from Advanced delivery" />
                            </ListItem>
                        </List>
                        <Typography variant="body2" paragraph>
                            Nothing you added yourself is touched. Two things we cannot remove
                            for you: the Boxphish sending domains and IPs in the Defender
                            portal, and the <strong>Boxphish Allowlisting Sync</strong> enterprise
                            application in Entra. Delete that app to withdraw our access entirely.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Takes a few minutes. You can connect again at any time.
                        </Typography>
                    </Box>

                    <Divider />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <Button onClick={() => setRevokeConfirmOpen(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={triggerAllowlistingRevoke} variant="contained" color="error">
                            Revoke allowlisting
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}